import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  CheckCircle,
  Target,
  Shield,
  Users,
  TrendingUp,
  Heart,
} from 'lucide-react';
import { WHATSAPP_URL } from '../lib/utils';
import { siteContentAPI, siteImagesAPI } from '../lib/api';
import CTASection from '../components/shared/CTASection';
import { usePageMeta } from '../components/shared/SEO';

// No external fallback images - use database images only

const valueIcons = [Shield, Target, Users, Heart, TrendingUp];
const defaultValues = [
  { title: 'Confiança', description: 'Construímos relacionamentos baseados em transparência e integridade.' },
  { title: 'Assertividade', description: 'Entregamos soluções precisas e eficazes para cada desafio.' },
  { title: 'Parceria', description: 'Trabalhamos lado a lado com nossos clientes, como um time.' },
  { title: 'Engajamento', description: 'Nos envolvemos genuinamente com o sucesso de cada cliente.' },
  { title: 'Otimização', description: 'Buscamos constantemente maneiras de melhorar resultados.' },
];

const AboutPage = () => {
  const [texts, setTexts] = useState({});
  const [siteImages, setSiteImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [contentRes, imagesRes] = await Promise.all([
          siteContentAPI.getAll().catch(() => ({ data: {} })),
          siteImagesAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setTexts(contentRes.data || {});
        const imgMap = {};
        (imagesRes.data || []).forEach(img => { imgMap[img.key] = img; });
        setSiteImages(imgMap);
      } catch (err) {
        console.error('Error loading about data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const t = (key, fallback) => texts[key] || fallback;

  const heroImgData = siteImages['about-hero'] || {};
  const heroImage = heroImgData.url || '';
  const heroPos = heroImgData.position || 'center';
  const heroFit = heroImgData.fit_mode || 'cover';

  const teamImgData = siteImages['about-team'] || {};
  const teamImage = teamImgData.url || '';
  const teamPos = teamImgData.position || 'center';
  const teamFit = teamImgData.fit_mode || 'cover';

  const milestones = [
    { number: t('about-page-milestone-1-number', '100+'), label: t('about-page-milestone-1-label', 'Clínicas atendidas') },
    { number: t('about-page-milestone-2-number', '5+'), label: t('about-page-milestone-2-label', 'Anos de experiência') },
    { number: t('about-page-milestone-3-number', '98%'), label: t('about-page-milestone-3-label', 'Satisfação dos clientes') },
    { number: t('about-page-milestone-4-number', 'R$ 10M+'), label: t('about-page-milestone-4-label', 'Em economia tributária gerada') },
  ];

  const differentials = [
    { title: t('about-page-diff-1-title', 'Especialização exclusiva em saúde'), desc: t('about-page-diff-1-desc', 'Não somos uma contabilidade genérica. Todo nosso time é especializado no setor médico.') },
    { title: t('about-page-diff-2-title', 'Abordagem consultiva'), desc: t('about-page-diff-2-desc', 'Não apenas executamos rotinas, mas orientamos decisões estratégicas.') },
    { title: t('about-page-diff-3-title', 'Tecnologia integrada'), desc: t('about-page-diff-3-desc', 'Usamos as melhores ferramentas para garantir eficiência e transparência.') },
    { title: t('about-page-diff-4-title', 'Foco em resultados'), desc: t('about-page-diff-4-desc', 'Medimos nosso sucesso pelos resultados que geramos para nossos clientes.') },
  ];

  usePageMeta({
    title: 'Quem Somos | Médico e Contadora Especializados em Gestão Médica - Apex Medical Hub',
    description: 'Conheça a Apex Medical Hub: equipe especializada em contabilidade e gestão para médicos, clínicas e consultórios. Sediados em Boa Vista/RR, atendemos todo o Brasil.',
    canonical: '/sobre',
  });

  if (loading) {
    return (
      <div data-testid="about-page" className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1e4e7e]"></div>
      </div>
    );
  }

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-white pt-28 pb-10" data-testid="about-hero">
        <div className="container-apex">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#007f73] font-medium mb-3 text-sm uppercase tracking-wider">
                {t('about-page-badge', 'Sobre a Apex Medical Hub')}
              </p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                {t('about-page-title', 'Parceiros estratégicos na estruturação do seu negócio médico')}
              </h1>
              <p className="text-slate-600 text-lg mb-8">
                {t('about-page-subtitle', 'A Apex Medical Hub nasceu da percepção de que médicos e clínicas precisavam de muito mais do que uma contabilidade tradicional. Precisavam de um parceiro estratégico que entendesse as particularidades do setor de saúde.')}
              </p>
              <Button asChild className="btn-primary rounded-md px-8" data-testid="about-cta">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  Falar com a Apex
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            <div className="relative">
              {heroImage && (
                <img
                  src={heroImage}
                  alt="Equipe Apex Medical Hub"
                  className="rounded-2xl shadow-xl w-full h-[400px]"
                  style={{ objectFit: heroFit, objectPosition: heroPos }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding" data-testid="about-story">
        <div className="container-apex">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
              {t('about-page-history-title', 'Nossa História')}
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {t('about-page-history-p1', 'A Apex Medical Hub foi fundada por profissionais que vivenciaram de perto os desafios de gestão de clínicas e consultórios médicos. Vimos médicos excepcionais perdendo dinheiro por falta de estrutura financeira adequada, clínicas crescendo sem controle e profissionais exaustos com a burocracia.')}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {t('about-page-history-p2', 'Decidimos criar uma empresa que oferecesse não apenas serviços contábeis, mas uma estrutura completa de backoffice pensada especificamente para a realidade do setor médico. Uma empresa que entendesse que cada real economizado em impostos é um real que pode ser reinvestido na qualidade do atendimento aos pacientes.')}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                {t('about-page-history-p3', 'Hoje, atendemos mais de 100 clínicas e consultórios em todo o Brasil, sempre com o mesmo compromisso: ser o parceiro estratégico que liberta médicos da burocracia para que possam se dedicar ao que fazem de melhor – cuidar de pessoas.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-slate-50" data-testid="about-values">
        <div className="container-apex">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-[#007f73] font-medium mb-3 text-sm uppercase tracking-wider">
              Nossos Valores
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">
              O que nos guia todos os dias
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {defaultValues.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border border-slate-100 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#1e4e7e]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#1e4e7e]" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-600">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Milestones Section */}
      <section className="section-padding bg-[#1e4e7e]" data-testid="about-milestones">
        <div className="container-apex">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="text-center">
                <p className="font-heading font-bold text-4xl md:text-5xl text-white mb-2">
                  {milestone.number}
                </p>
                <p className="text-white/70">
                  {milestone.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="section-padding" data-testid="about-differentials">
        <div className="container-apex">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              {teamImage && (
                <img
                  src={teamImage}
                  alt="Ambiente de trabalho Apex"
                  className="rounded-2xl shadow-xl w-full h-[400px]"
                  style={{ objectFit: teamFit, objectPosition: teamPos }}
                />
              )}
            </div>
            <div>
              <p className="text-[#007f73] font-medium mb-3 text-sm uppercase tracking-wider">
                Por que a Apex
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('about-page-diff-title', 'O que nos diferencia')}
              </h2>
              <ul className="space-y-4">
                {differentials.map((diff, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-[#007f73] mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{diff.title}</h4>
                      <p className="text-slate-600">{diff.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Pronto para conhecer a Apex?"
        description="Agende uma conversa sem compromisso e descubra como podemos ajudar sua clínica."
        buttonText="Falar com especialista"
      />
    </div>
  );
};

export default AboutPage;
